/**
 * Permission Audit Logger - Modular Entry Point
 *
 * Comprehensive audit logging for permission checks and security events
 */

import type { AuditLevel, PermissionActionType, AuditEvent, AuditQuery, AuditStats } from './audit-logger/types';
import { SecurityEvent } from '../permission-integrity';
import {
  logPermissionCheck,
  logSecurityEvent,
  logAccessAttempt,
  logForcedLogout,
  LoggingContext
} from './audit-logger/logging-methods';
import { queryEvents, generateStats, exportForCompliance, convertToCSV } from './audit-logger/query-and-stats';
import {
  generateSessionId,
  hashUserId,
  hashResourceId,
  getUserAgent,
  mapSecurityLevel,
  generateComplianceFlags,
  shouldLog,
  createAuditEvent
} from './audit-logger/utility-functions';
import { SiemIntegration } from './audit-logger/siem-integration';
import { CleanupManager } from './audit-logger/cleanup-manager';

export class PermissionAuditLogger {
  private auditLevel: AuditLevel;
  private events: AuditEvent[] = [];
  private sessionId: string;
  private anonymizeData: boolean;
  private siemIntegration: SiemIntegration;
  private cleanupManager: CleanupManager;

  constructor(
    auditLevel: AuditLevel = 'INFO',
    anonymizeData: boolean = false
  ) {
    this.auditLevel = auditLevel;
    this.anonymizeData = anonymizeData;
    this.sessionId = generateSessionId();
    this.siemIntegration = new SiemIntegration(auditLevel);
    this.cleanupManager = new CleanupManager(auditLevel);
  }

  /**
   * Create logging context for method calls
   */
  private getLoggingContext(): LoggingContext {
    return {
      anonymizeData: this.anonymizeData,
      sessionId: this.sessionId,
      auditLevel: this.auditLevel,
      shouldLog: (action: PermissionActionType, result: string) =>
        shouldLog('PRODUCTION', action, result),
      createAuditEvent: (params: Partial<AuditEvent>) =>
        createAuditEvent(this.sessionId, params as Partial<AuditEvent> & {
          action: PermissionActionType;
          level: AuditLevel;
          result: 'SUCCESS' | 'FAILURE' | 'DENIED';
          details: Record<string, unknown>;
          environment: 'development' | 'staging' | 'production';
        }),
      addEvent: (event: AuditEvent) => this.addEvent(event),
      sendToSiem: (event: AuditEvent) => this.siemIntegration.sendToSiem(event),
      hashUserId,
      hashResourceId,
      getUserAgent,
      generateComplianceFlags,
      mapSecurityLevel
    };
  }

  /**
   * Log a permission check event
   */
  logPermissionCheck(
    userId: string | undefined,
    permission: string,
    resource?: string,
    resourceId?: string,
    result: 'SUCCESS' | 'FAILURE' | 'DENIED' = 'SUCCESS',
    reason?: string,
    metadata: Record<string, unknown> = {}
  ): void {
    logPermissionCheck(
      this.getLoggingContext(),
      userId,
      permission,
      resource,
      resourceId,
      result,
      reason,
      metadata
    );
  }

  /**
   * Log a security event from the integrity service
   */
  logSecurityEvent(securityEvent: SecurityEvent): void {
    logSecurityEvent(this.getLoggingContext(), securityEvent);
  }

  /**
   * Log access attempt (successful or failed)
   */
  logAccessAttempt(
    userId: string | undefined,
    resource: string,
    resourceId?: string,
    result: 'SUCCESS' | 'FAILURE' | 'DENIED' = 'SUCCESS',
    reason?: string,
    metadata: Record<string, unknown> = {}
  ): void {
    logAccessAttempt(
      this.getLoggingContext(),
      userId,
      resource,
      resourceId,
      result,
      reason,
      metadata
    );
  }

  /**
   * Log forced logout due to security violation
   */
  logForcedLogout(
    userId: string | undefined,
    reason: string,
    metadata: Record<string, unknown> = {}
  ): void {
    logForcedLogout(this.getLoggingContext(), userId, reason, metadata);
  }

  /**
   * Generic log event method
   */
  logEvent(
    level: AuditLevel,
    action: PermissionActionType,
    details: Record<string, unknown> = {}
  ): void {
    const event = createAuditEvent(this.sessionId, {
      action,
      level,
      result: 'SUCCESS',
      details,
      environment: 'production'
    });
    this.events.push(event);
  }

  /**
   * Log security violations
   */
  logSecurityViolation(
    violationType: string,
    details: Record<string, unknown> = {}
  ): void {
    const event = createAuditEvent(this.sessionId, {
      action: 'SECURITY_VIOLATION',
      level: 'CRITICAL',
      result: 'FAILURE',
      details: { violationType, ...details },
      environment: 'production'
    });
    this.events.push(event);
  }

  /**
   * Query audit events
   */
  queryEvents(query: AuditQuery = {}): AuditEvent[] {
    return queryEvents(this.events, this.anonymizeData, hashUserId, query);
  }

  /**
   * Generate audit statistics
   */
  generateStats(timeRangeHours: number = 24): AuditStats {
    return generateStats(this.events, timeRangeHours);
  }

  /**
   * Export audit logs for compliance
   */
  exportForCompliance(
    startDate: Date,
    endDate: Date,
    format: 'JSON' | 'CSV' = 'JSON'
  ): string {
    return exportForCompliance(
      this.events,
      this.auditLevel,
      this.anonymizeData,
      (query) => this.queryEvents(query),
      convertToCSV,
      startDate,
      endDate,
      format
    );
  }

  /**
   * Clear old audit events to manage memory
   */
  cleanup(): void {
    this.events = this.cleanupManager.cleanup(this.events);
  }

  /**
   * Add event to storage and trigger cleanup if needed
   */
  private addEvent(event: AuditEvent): void {
    this.events.push(event);

    // Trigger cleanup if we're getting close to the limit
    if (this.cleanupManager.shouldTriggerCleanup(this.events.length)) {
      this.cleanup();
    }
  }
}

// Export singleton instance with INFO level
export const auditLogger = new PermissionAuditLogger('INFO');

// Re-export types for convenience
export type { AuditLevel, PermissionActionType, AuditEvent, AuditQuery, AuditStats };